// Lab 1: exactly-once RPC over an unreliable network.
// Client retries carry the original (client, seq) identity; the server's AMO
// table executes newer sequences once, answers duplicates from cache, and
// ignores stale ones. The client accepts only the reply matching its one
// outstanding call.
//
// config: { clients, server, linkDelay=1, retryTimeout=5 }
import { EventKind } from '../../core/types';
import { amoApply } from '../common/amo';
import { createKvStore, kvApply } from '../common/kvStore';
import { checkLab1Invariants } from './invariants';

export const MSG_REQUEST = 'request';
export const MSG_REPLY = 'reply';
export const TIMER_RETRY = 'retry';

export const labMeta = {
  id: 'lab1',
  title: 'Lab 1 · Exactly-once RPC',
  description: 'Retries plus deduplication turn an unreliable network into one application effect per call.',
};

export function defaultConfig() {
  return { clients: ['C1'], server: 'S', linkDelay: 1, retryTimeout: 5 };
}

export function initialize(config) {
  const nodes = {};
  config.clients.forEach((id) => {
    nodes[id] = {
      durable: {
        role: 'client',
        nextSeq: 1,
        accepted: 0,
        sentIds: [],
        completed: [],
      },
      volatile: { outstanding: null },
    };
  });
  nodes[config.server] = {
    durable: { role: 'server', kv: createKvStore(), amo: {}, appliedIds: [] },
    volatile: {},
  };
  return { nodes, initialEvents: [] };
}

export function freshVolatile(durable) {
  if (durable.role === 'client') return { outstanding: null };
  return {};
}

function clone(part) {
  return JSON.parse(JSON.stringify(part));
}

function invoke(state, config, nodeId, command) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  if (volatile.outstanding) {
    return { durable, volatile, note: 'client busy; command ignored' };
  }
  const seq = durable.nextSeq;
  durable.nextSeq += 1;
  durable.accepted += 1;
  const id = nodeId + '#' + seq;
  durable.sentIds.push(id);
  volatile.outstanding = { seq, command };
  return {
    durable,
    volatile,
    outbox: [
      {
        to: config.server,
        payload: { msgType: MSG_REQUEST, client: nodeId, seq, command },
        delay: config.linkDelay,
        label: 'request ' + id,
      },
    ],
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_RETRY + ':' + seq,
        timerType: TIMER_RETRY,
        payload: { seq },
        delay: config.retryTimeout,
        label: 'retry ' + id,
      },
    ],
    note: 'invoke seq ' + seq,
  };
}

function onRequest(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const { client, seq, command } = event.payload;
  const id = client + '#' + seq;
  const outcome = amoApply(durable.amo, client, seq, () => {
    durable.appliedIds.push(id);
    return kvApply(durable.kv, command);
  });
  if (outcome.status === 'stale') {
    return { durable, note: 'stale request ' + id + ' ignored' };
  }
  return {
    durable,
    outbox: [
      {
        to: client,
        payload: {
          msgType: MSG_REPLY,
          client,
          seq,
          result: outcome.result,
          cached: outcome.status === 'duplicate',
        },
        delay: config.linkDelay,
        label: 'reply ' + id + (outcome.status === 'duplicate' ? ' (cached)' : ''),
      },
    ],
    note:
      outcome.status === 'duplicate'
        ? 'duplicate request ' + id + '; resend cached result'
        : 'execute request ' + id,
  };
}

function onReply(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const { seq, result } = event.payload;
  if (volatile.outstanding && volatile.outstanding.seq === seq) {
    durable.completed.push({ seq, result });
    volatile.outstanding = null;
    return {
      durable,
      volatile,
      timerOps: [{ action: 'cancel', timerKey: TIMER_RETRY + ':' + seq }],
      note: 'complete seq ' + seq,
    };
  }
  return { durable, volatile, note: 'stale reply seq ' + seq + ' ignored' };
}

function onRetry(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const { seq } = event.payload;
  if (volatile.outstanding && volatile.outstanding.seq === seq) {
    return {
      durable,
      volatile,
      outbox: [
        {
          to: config.server,
          payload: {
            msgType: MSG_REQUEST,
            client: nodeId,
            seq,
            command: volatile.outstanding.command,
          },
          delay: config.linkDelay,
          label: 'request ' + nodeId + '#' + seq + ' (retry)',
        },
      ],
      timerOps: [
        {
          action: 'set',
          timerKey: TIMER_RETRY + ':' + seq,
          timerType: TIMER_RETRY,
          payload: { seq },
          delay: config.retryTimeout,
          label: 'retry ' + nodeId + '#' + seq,
        },
      ],
      note: 'timeout; resend seq ' + seq + ' with same identity',
    };
  }
  return { durable, volatile, note: 'stale retry timer discarded' };
}

export function handleClientCommand(state, nodeId, event) {
  const node = state.nodes[nodeId];
  if (!node || node.durable.role !== 'client') {
    return { note: 'invoke sent to non-client; ignored' };
  }
  return invoke(state, state.config, nodeId, event.payload.command);
}

export function handleMessage(state, nodeId, event) {
  const node = state.nodes[nodeId];
  if (!node) return { note: 'message to unknown node; ignored' };
  if (event.payload.msgType === MSG_REQUEST && node.durable.role === 'server') {
    return onRequest(state, state.config, nodeId, event);
  }
  if (event.payload.msgType === MSG_REPLY && node.durable.role === 'client') {
    return onReply(state, state.config, nodeId, event);
  }
  return { note: 'unexpected ' + event.payload.msgType + ' at ' + nodeId };
}

export function handleTimer(state, nodeId, event) {
  if (event.payload.timerType === TIMER_RETRY) {
    return onRetry(state, state.config, nodeId, event);
  }
  return { note: 'unknown timer; ignored' };
}

export function checkInvariants(state) {
  return checkLab1Invariants(state);
}

export function describeEvent(event) {
  const p = event.payload || {};
  if (event.kind === EventKind.MESSAGE) {
    if (p.msgType === MSG_REQUEST) return 'deliver request ' + p.client + '#' + p.seq;
    if (p.msgType === MSG_REPLY) {
      return 'deliver reply ' + p.client + '#' + p.seq + (p.cached ? ' (cached)' : '');
    }
    return 'deliver message';
  }
  if (event.kind === EventKind.TIMER) return 'fire timer ' + (p.timerKey || '');
  if (event.kind === EventKind.CLIENT_COMMAND) return 'invoke command';
  return event.label || event.kind;
}

export function summarizeNode(nodeId, node) {
  const { durable, volatile } = node;
  if (durable.role === 'client') {
    return [
      ['role', 'client'],
      ['next seq', String(durable.nextSeq)],
      ['accepted', String(durable.accepted)],
      [
        'outstanding',
        volatile.outstanding
          ? '#' + volatile.outstanding.seq + ' ' + JSON.stringify(volatile.outstanding.command)
          : 'none',
      ],
      ['completed', String(durable.completed.length)],
    ];
  }
  return [
    ['role', 'server'],
    ['keys', Object.keys(durable.kv).join(', ') || '(empty)'],
    ...Object.keys(durable.kv).map((k) => ['  ' + k, JSON.stringify(durable.kv[k])]),
    ['executions', String(durable.appliedIds.length)],
    [
      'amo',
      Object.keys(durable.amo)
        .map((c) => c + ':#' + durable.amo[c].seq)
        .join(', ') || '(empty)',
    ],
  ];
}


export function sampleCommands(config) {
  const client = config.clients[0];
  return [
    { label: 'PUT x = 1', to: client, command: { op: 'put', key: 'x', value: 1 } },
    { label: 'PUT y = 2', to: client, command: { op: 'put', key: 'y', value: 2 } },
    { label: 'GET x', to: client, command: { op: 'get', key: 'x' } },
    { label: 'APPEND x + "!"', to: client, command: { op: 'append', key: 'x', value: '!' } },
  ];
}

const lab1Adapter = {
  initialize,
  handleMessage,
  handleClientCommand,
  handleTimer,
  freshVolatile,
  checkInvariants,
  describeEvent,
  summarizeNode,
};

export default lab1Adapter;
